import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useSignupLeadsTab,
  type UseSignupLeadsTabResult,
} from '@/features/users/hooks/useSignupLeadsTab.hook';
import { useDebouncedValue } from '@/hooks/useDebouncedValue.hook';
import { studentsApi } from '@/services/students.service';
import type {
  CampusFilterOption,
  StudentDetail,
  StudentListFilter,
  StudentListItem,
  StudentListResult,
  StudentListSort,
} from '@/types/student.types';

const PAGE_SIZE = 10;

export const STUDENTS_QUERY_KEY = 'students';
export const CAMPUS_OPTIONS_QUERY_KEY = ['students', 'campuses'] as const;

interface UseUsersListPageResult {
  /** True while the "Didn't Finish Signup" tab is showing signup leads instead of users. */
  isLeadsTab: boolean;
  /** True on the Deleted tab, where rows are retained records rather than members. */
  isDeletedTab: boolean;
  leads: UseSignupLeadsTabResult;
  // data
  users: StudentListItem[];
  campuses: CampusFilterOption[];
  campusNameById: Record<string, string>;
  total: number;
  page: number;
  totalPages: number;
  // ui state
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  errorMessage: string | null;
  // filter inputs
  filter: StudentListFilter;
  search: string;
  campusId: string;
  sort: StudentListSort;
  // mutation tracking
  suspendingId: string | null;
  // detail drawer
  selectedUser: StudentListItem | null;
  detailData: StudentDetail | undefined;
  isLoadingDetail: boolean;
  isErrorDetail: boolean;
  // handlers
  onChangeFilter: (filter: StudentListFilter) => void;
  onChangeSearch: (value: string) => void;
  onChangeCampus: (id: string) => void;
  onChangeSort: (sort: StudentListSort) => void;
  onChangePage: (page: number) => void;
  onView: (user: StudentListItem) => void;
  onCloseDetail: () => void;
  onSuspend: (user: StudentListItem) => void;
  onActivate: (user: StudentListItem) => void;
}

export const useUsersListPage = (): UseUsersListPageResult => {
  const queryClient = useQueryClient();

  // The dashboard's "Incomplete Onboarding" card links straight into this tab.
  const [searchParams] = useSearchParams();
  const initialFilter: StudentListFilter =
    searchParams.get('tab') === 'incomplete' ? 'incomplete' : 'all';

  // Filter / search / sort / page state
  const [filter, setFilter] = useState<StudentListFilter>(initialFilter);
  const [search, setSearch] = useState('');
  const [campusId, setCampusId] = useState('');
  const [sort, setSort] = useState<StudentListSort>('newest');
  const [page, setPage] = useState(1);

  const [suspendingId, setSuspendingId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<StudentListItem | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);

  const campusOptionsQuery = useQuery<CampusFilterOption[]>({
    queryKey: CAMPUS_OPTIONS_QUERY_KEY,
    queryFn: () => studentsApi.listCampusOptions(),
    staleTime: 5 * 60 * 1000,
  });

  const campuses = useMemo(() => campusOptionsQuery.data ?? [], [campusOptionsQuery.data]);
  const campusNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of campuses) map[c.id] = c.name;
    return map;
  }, [campuses]);

  const isLeadsTab = filter === 'incomplete';
  const isDeletedTab = filter === 'deleted';

  const leads = useSignupLeadsTab({
    enabled: isLeadsTab,
    search: debouncedSearch,
    campusId,
    page,
    limit: PAGE_SIZE,
  });

  const listQuery = useQuery<StudentListResult>({
    queryKey: [
      STUDENTS_QUERY_KEY,
      { filter, search: debouncedSearch, campusId, sort, page, limit: PAGE_SIZE },
    ],
    queryFn: () =>
      studentsApi.list({
        filter,
        search: debouncedSearch || undefined,
        campusId: campusId || undefined,
        sort,
        page,
        limit: PAGE_SIZE,
      }),
    // `incomplete` is not a filter this endpoint understands — that tab reads a different
    // collection entirely, so the users query is switched off rather than sent a bad value.
    enabled: !isLeadsTab,
    placeholderData: (previous) => previous,
  });

  const detailQuery = useQuery<StudentDetail>({
    queryKey: ['student', selectedUser?.id],
    queryFn: () => studentsApi.getById(selectedUser!.id),
    enabled: !!selectedUser?.id,
    staleTime: 30_000,
    retry: 1,
  });

  const invalidateList = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: [STUDENTS_QUERY_KEY] }).then(() => undefined);

  const suspendMutation = useMutation<StudentListItem, unknown, { id: string; suspend: boolean }>({
    mutationFn: ({ id, suspend }) =>
      studentsApi.updateAccountStatus(id, suspend ? 'suspended' : 'active'),
    onMutate: ({ id }) => setSuspendingId(id),
    onSettled: (_data, _error, variables) => {
      setSuspendingId(null);
      void invalidateList();
      // Refresh the detail drawer if this user is currently open.
      void queryClient.invalidateQueries({ queryKey: ['student', variables.id] });
    },
  });

  // Reset to page 1 whenever filter inputs change to avoid landing on an out-of-range page.
  const resetAndSet =
    <T,>(setter: (value: T) => void) =>
    (value: T): void => {
      setter(value);
      setPage(1);
    };

  return {
    isLeadsTab,
    isDeletedTab,
    leads,
    users: listQuery.data?.items ?? [],
    campuses,
    campusNameById,
    total: isLeadsTab ? leads.total : (listQuery.data?.total ?? 0),
    page: isLeadsTab ? leads.page : (listQuery.data?.page ?? page),
    totalPages: isLeadsTab ? leads.totalPages : (listQuery.data?.totalPages ?? 1),
    isLoading: isLeadsTab ? leads.isLoading : listQuery.isLoading,
    isFetching: listQuery.isFetching,
    isError: isLeadsTab ? leads.isError : listQuery.isError,
    errorMessage: isLeadsTab
      ? leads.errorMessage
      : listQuery.error instanceof Error
        ? listQuery.error.message
        : null,
    filter,
    search,
    campusId,
    sort,
    suspendingId,
    selectedUser,
    detailData: detailQuery.data,
    isLoadingDetail: detailQuery.isLoading && detailQuery.fetchStatus !== 'idle',
    isErrorDetail: detailQuery.isError,
    onChangeFilter: resetAndSet((next: StudentListFilter) => {
      leads.onClearSelection();
      setFilter(next);
    }),
    onChangeSearch: resetAndSet(setSearch),
    onChangeCampus: resetAndSet(setCampusId),
    onChangeSort: resetAndSet(setSort),
    onChangePage: setPage,
    onView: (user) => setSelectedUser(user),
    onCloseDetail: () => setSelectedUser(null),
    onSuspend: (user) => suspendMutation.mutate({ id: user.id, suspend: true }),
    onActivate: (user) => suspendMutation.mutate({ id: user.id, suspend: false }),
  };
};
